import { BidParamsMapping, CreateBidParamsMapping } from '@pubstack/common/src/bid-params-mapping'
import { BidderParamValidation, BidderParamValidationType } from '@pubstack/common/src/bidderParamValidation'
import { BidParamsMappingUpload } from '@pubstack/common/src/bid-params-mapping-upload'
import { gzip } from 'pako'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { buildUrlWithSearchParams } from '~/utils/url'
import { MutationToasts, useLazyQuery, useMutation, useQuery } from './api-client'
import { Product } from '~/hooks/useProduct'

export const BID_PARAMS_MAPPINGS_QUERY_KEYS = ['bid-params-mappings']
const BID_PARAMS_UPLOADS_QUERY_KEYS = [...BID_PARAMS_MAPPINGS_QUERY_KEYS, 'uploads']

export const useDefaultMappings = (filters: { bidderSeatId: string; scopeId: string }) => {
  const url = buildUrlWithSearchParams('/pbs/bid-params-mappings', {
    ...filters,
  })

  const queryResult = useQuery<BidParamsMapping[]>({
    url,
    keys: [...BID_PARAMS_MAPPINGS_QUERY_KEYS, filters.bidderSeatId || '', filters.scopeId || ''],
    enabled: !!filters.bidderSeatId && !!filters.scopeId,
  })

  return { ...queryResult, data: queryResult.data || [] }
}

/** One empty list for every render without data, so a memo over a disabled query holds. */
const NO_MAPPINGS: BidParamsMapping[] = []

export const useAllBidParamsMappings = (product: Product, bidderSeatIds: string[], siteId?: string) => {
  const url = buildUrlWithSearchParams(`/${product}/bid-params-mappings`, {
    bidderSeatIds,
    siteId,
  })
  const queryResult = useQuery<BidParamsMapping[]>({
    url,
    keys: [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS, ...(siteId ? [siteId] : []), ...(bidderSeatIds ? [bidderSeatIds] : [])],
    enabled: !!bidderSeatIds.length,
  })
  return { ...queryResult, data: queryResult.data ?? NO_MAPPINGS }
}

export const useBidParamsMappingsByBidderSeatId = (product: Product, filters: { bidderSeatId?: string; siteId?: string }, options?: { enabled?: boolean }) => {
  const url = buildUrlWithSearchParams(`/${product}/bid-params-mappings`, {
    ...filters,
  })

  const queryResult = useQuery<BidParamsMapping[]>({
    url,
    keys: [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS, ...Object.values(filters)],
    // Site-scoped callers gate on site selection via `options.enabled`; default is enabled.
    enabled: (options?.enabled ?? true) && !!filters.bidderSeatId,
  })

  // We want to memoize the data to avoid unnecessary re-renders due to this particular behavior of returning an empty array when either bidderSeatId or siteId is missing
  const memoizedData = useMemo(() => {
    if (!filters.bidderSeatId) return []
    return queryResult.data || []
  }, [queryResult.data, filters.bidderSeatId])

  return { ...queryResult, data: memoizedData }
}

export const useUpsertBidParamsMapping = (product: Product, bidderSeatId: string, toasts: MutationToasts = {}) => {
  return useMutation<(BidParamsMapping | CreateBidParamsMapping)[]>({
    method: 'put',
    url: `/${product}/bid-params-mappings?bidderSeatId=${bidderSeatId}`,
    collectionKeys: [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS, bidderSeatId],
    ...toasts,
  })
}

export const useBulkUpsertBidParamsMapping = (product: Product, toasts: MutationToasts = {}) => {
  return useMutation<(BidParamsMapping | CreateBidParamsMapping)[]>({
    method: 'put',
    url: `/${product}/bid-params-mappings`,
    collectionKeys: [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS],
    ...toasts,
  })
}

export const useUpsertBidParamsMappingAsCsv = (product: Product, bidderSeatId: string, toasts: MutationToasts = {}, siteId?: string) => {
  return useMutation<FormData, { uploadId: string }>({
    method: 'put',
    url: `/${product}/bid-params-mappings/csv?bidderSeatId=${bidderSeatId}${siteId ? `&siteId=${siteId}` : ''}`,
    collectionKeys: [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS],
    ...toasts,
  })
}

export type BidParamsMappingsCsvDownload = { url: string; filename: string }

export const useGetTemplateFile = (product: Product, bidderSeatId: string, siteId?: string) => {
  return useLazyQuery<BidParamsMappingsCsvDownload | Buffer<ArrayBufferLike>>({
    url: `/${product}/bid-params-mappings/csv?bidderSeatId=${bidderSeatId}&templateOnly=true${siteId ? `&siteId=${siteId}` : ''}`,
    keys: [product, 'bid-params-mappings', 'csv', bidderSeatId, 'template', ...(siteId ? [siteId] : [])],
  })
}

/**
 * `onlyStackedAdUnits` narrows the file to the ad units each media's stacks deploy, matching what the
 * mapping filter shows on screen. Its own query key, so the filtered and full files never serve each
 * other from the cache.
 */
export const useGetMappingFile = (product: Product, bidderSeatId: string, siteId?: string, onlyStackedAdUnits?: boolean) => {
  return useLazyQuery<BidParamsMappingsCsvDownload | Buffer<ArrayBufferLike>>({
    url: `/${product}/bid-params-mappings/csv?bidderSeatId=${bidderSeatId}${siteId ? `&siteId=${siteId}` : ''}${onlyStackedAdUnits ? '&onlyStackedAdUnits=true' : ''}`,
    keys: [product, 'bid-params-mappings', 'csv', bidderSeatId, 'mapping', ...(siteId ? [siteId] : []), ...(onlyStackedAdUnits ? ['stacked'] : [])],
  })
}

export const useUploads = (product: Product, scopeId: string) => {
  const query = useQuery<BidParamsMappingUpload[]>({
    url: `/${product}/bid-params-uploads?status=PENDING,PROCESSING,FAILED`,
    keys: [product, ...BID_PARAMS_UPLOADS_QUERY_KEYS, 'active'],
    enabled: !!scopeId,
    refetchInterval: (q) => {
      const hasActive = q.state.data?.some((u) => u.status === 'PENDING' || u.status === 'PROCESSING')
      return hasActive ? 3000 : false
    },
  })

  const activeUploads = query.data?.filter((u) => u.status === 'PENDING' || u.status === 'PROCESSING') ?? []
  const failedUploads = query.data?.filter((u) => u.status === 'FAILED') ?? []

  return { ...query, activeUploads, failedUploads }
}

// Poll a single upload by id until it reaches a terminal status (COMPLETED/FAILED), which the
// active-list query above never returns. Disabled when no upload is being tracked.
const useUploadStatus = (product: Product, uploadId: string | null) =>
  useQuery<BidParamsMappingUpload>({
    url: `/${product}/bid-params-uploads/${uploadId}`,
    keys: [product, ...BID_PARAMS_UPLOADS_QUERY_KEYS, 'by-id', uploadId ?? ''],
    enabled: !!uploadId,
    refetchInterval: (q) => (q.state.data?.status === 'COMPLETED' || q.state.data?.status === 'FAILED' ? false : 1500),
  })

export const useAcknowledgeUpload = (product: Product, uploadId: string) => {
  return useMutation({
    method: 'patch',
    url: `/${product}/bid-params-uploads/${uploadId}/acknowledge`,
    // Upload queries are keyed [product, ...BID_PARAMS_UPLOADS_QUERY_KEYS, ...]
    collectionKeys: [product, ...BID_PARAMS_UPLOADS_QUERY_KEYS],
  })
}

// Upload state machine types
type MappingUploadState =
  | { status: 'idle' }
  | { status: 'submitting'; file: File }
  | { status: 'processing'; file: File }
  | { status: 'success' }
  | { status: 'error'; errors: BidderParamValidation<BidderParamValidationType>[]; message?: string }

type UseMappingUploadOptions = {
  product: Product
  bidderSeatId: string
  scopeId: string
  onSuccess?: () => void
  onError?: (errors: BidderParamValidation<BidderParamValidationType>[], message?: string) => void
}

export const useMappingUpload = ({ product, bidderSeatId, scopeId, onSuccess, onError }: UseMappingUploadOptions) => {
  const { activeUploads } = useUploads(product, scopeId)
  const { mutateAsync: upsertCsv } = useUpsertBidParamsMappingAsCsv(product, bidderSeatId)

  const [uploadState, setUploadState] = useState<MappingUploadState>({ status: 'idle' })
  const [trackedUploadId, setTrackedUploadId] = useState<string | null>(null)

  // Poll the submitted upload by its own id. Unlike the active list (which only ever returns
  // PENDING/PROCESSING/FAILED), this positively detects COMPLETED, so a fast upload that finishes
  // between two active-list polls no longer leaves the UI stuck on "Uploading…".
  const { data: trackedUpload } = useUploadStatus(product, trackedUploadId)

  // Find uploads for this specific bidder seat (bidderSeatId is unique, no need to filter on isInternal)
  const activeUpload = activeUploads.find((u) => u.bidderSeatId === bidderSeatId)

  // Resolve success/error from the tracked upload's own terminal status.
  useEffect(() => {
    /* oxlint-disable react/set-state-in-effect -- upload state-machine: setState transitions sync local status from the polled upload */
    if (!trackedUploadId || trackedUpload?.id !== trackedUploadId) return

    if (trackedUpload.status === 'COMPLETED') {
      setTrackedUploadId(null)
      setUploadState({ status: 'success' })
      onSuccess?.()
    } else if (trackedUpload.status === 'FAILED') {
      setTrackedUploadId(null)
      const errors = trackedUpload.validationErrors ?? []
      setUploadState({ status: 'error', errors, message: trackedUpload.errorMessage })
      onError?.(errors, trackedUpload.errorMessage)
    }
    /* oxlint-enable react/set-state-in-effect */
  }, [trackedUploadId, trackedUpload, onSuccess, onError])

  const submitUpload = useCallback(
    async (file: File) => {
      setUploadState({ status: 'processing', file })

      try {
        const data = new FormData()

        // Compress CSV before sending to avoid 413 Content Too Large errors
        const arrayBuffer = await file.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)
        const compressed = gzip(uint8Array)
        const compressedBlob = new Blob([compressed], { type: 'application/gzip' })

        data.append('csv', compressedBlob, file.name + '.gz')
        const { uploadId } = await upsertCsv(data)
        setTrackedUploadId(uploadId)
      } catch (e) {
        setUploadState({ status: 'error', errors: [], message: 'An error occurred while submitting the mapping.' })
        throw e
      }
    },
    [upsertCsv]
  )

  const reset = useCallback(() => {
    setUploadState({ status: 'idle' })
    setTrackedUploadId(null)
  }, [])

  return {
    uploadState,
    submitUpload,
    reset,
    isLoading: uploadState.status === 'submitting' || uploadState.status === 'processing',
    currentFile: uploadState.status === 'submitting' || uploadState.status === 'processing' ? uploadState.file : null,
    // Expose upload info for UI blocking states
    activeUpload,
    activeUploads,
  }
}
